import type { Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import { useEditorState } from '@tiptap/react'
import { Separator } from '@/components/ui/separator'
import {
  Bold, Italic, Strikethrough, Code, Link, Unlink,
  Heading1, Heading2, Heading3, List, ListOrdered, MessageSquare
} from 'lucide-react'
import { useState, useCallback } from 'react'
import type { Comment } from '../../hooks/useComments'

interface Props {
  editor: Editor
  noteSlug?: string
  onAddComment?: (anchorText: string, body: string) => Promise<Comment | null>
}

export default function BubbleMenuBar({ editor, noteSlug, onAddComment }: Props) {
  const [showLinkInput, setShowLinkInput] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [showCommentInput, setShowCommentInput] = useState(false)
  const [commentBody, setCommentBody] = useState('')
  const [commentAnchor, setCommentAnchor] = useState('')

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e.isActive('bold'),
      isItalic: e.isActive('italic'),
      isStrike: e.isActive('strike'),
      isCode: e.isActive('code'),
      isLink: e.isActive('link'),
      isH1: e.isActive('heading', { level: 1 }),
      isH2: e.isActive('heading', { level: 2 }),
      isH3: e.isActive('heading', { level: 3 }),
      isBulletList: e.isActive('bulletList'),
      isOrderedList: e.isActive('orderedList'),
    }),
  })

  const btn = (active: boolean) =>
    `h-7 w-7 flex items-center justify-center rounded transition-colors ${
      active
        ? 'bg-gray-900/10 text-gray-900'
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
    }`

  const handleLinkSubmit = useCallback(() => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run()
    }
    setLinkUrl('')
    setShowLinkInput(false)
  }, [editor, linkUrl])

  const handleUnlink = useCallback(() => {
    editor.chain().focus().unsetLink().run()
    setShowLinkInput(false)
  }, [editor])

  const handleCommentClick = useCallback(() => {
    const { from, to } = editor.state.selection
    if (from === to) return
    const selectedText = editor.state.doc.textBetween(from, to, ' ')
    setCommentAnchor(selectedText)
    setShowCommentInput(true)
  }, [editor])

  const handleCommentSubmit = useCallback(async () => {
    if (!commentBody.trim() || !onAddComment) return
    const comment = await onAddComment(commentAnchor, commentBody.trim())
    if (comment) {
      const { from, to } = editor.state.selection
      editor.chain().focus().setMark('comment', { commentId: comment.id }).run()
    }
    setCommentBody('')
    setCommentAnchor('')
    setShowCommentInput(false)
  }, [editor, commentBody, commentAnchor, onAddComment])

  return (
    <BubbleMenu
      editor={editor}
      options={{
        placement: 'top',
        offset: { mainAxis: 10 },
      }}
    >
      <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white backdrop-blur-sm px-1 py-0.5 shadow-lg">
        {showCommentInput ? (
          <div className="flex items-center gap-1 px-1">
            <input
              type="text"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCommentSubmit()
                }
                if (e.key === 'Escape') {
                  setShowCommentInput(false)
                  setCommentBody('')
                  setCommentAnchor('')
                }
              }}
              placeholder="Add a comment..."
              className="w-52 rounded bg-gray-50 px-2.5 py-1 text-xs text-gray-900 outline-none placeholder:text-gray-400 border border-gray-200 focus:border-amber-400"
              autoFocus
            />
            <button
              className={btn(false)}
              onClick={handleCommentSubmit}
              disabled={!commentBody.trim()}
            >
              <MessageSquare className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : showLinkInput ? (
          <div className="flex items-center gap-1 px-1">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleLinkSubmit()
                }
                if (e.key === 'Escape') {
                  setShowLinkInput(false)
                  setLinkUrl('')
                }
              }}
              placeholder="Paste link..."
              className="w-52 rounded bg-gray-50 px-2.5 py-1 text-xs text-gray-900 outline-none placeholder:text-gray-400 border border-gray-200 focus:border-gray-400"
              autoFocus
            />
            <button className={btn(false)} onClick={handleLinkSubmit}>
              <Link className="h-3.5 w-3.5" />
            </button>
            {state.isLink && (
              <button
                className="h-7 w-7 flex items-center justify-center rounded text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                onClick={handleUnlink}
              >
                <Unlink className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ) : (
          <>
            <button className={btn(state.isH1)}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
              <Heading1 className="h-3.5 w-3.5" />
            </button>
            <button className={btn(state.isH2)}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
              <Heading2 className="h-3.5 w-3.5" />
            </button>
            <button className={btn(state.isH3)}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
              <Heading3 className="h-3.5 w-3.5" />
            </button>

            <Separator orientation="vertical" className="mx-0.5 h-4 bg-gray-200" />

            <button className={btn(state.isBold)}
              onClick={() => editor.chain().focus().toggleBold().run()}>
              <Bold className="h-3.5 w-3.5" />
            </button>
            <button className={btn(state.isItalic)}
              onClick={() => editor.chain().focus().toggleItalic().run()}>
              <Italic className="h-3.5 w-3.5" />
            </button>
            <button className={btn(state.isStrike)}
              onClick={() => editor.chain().focus().toggleStrike().run()}>
              <Strikethrough className="h-3.5 w-3.5" />
            </button>
            <button className={btn(state.isCode)}
              onClick={() => editor.chain().focus().toggleCode().run()}>
              <Code className="h-3.5 w-3.5" />
            </button>

            <Separator orientation="vertical" className="mx-0.5 h-4 bg-gray-200" />

            <button className={btn(state.isBulletList)}
              onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <List className="h-3.5 w-3.5" />
            </button>
            <button className={btn(state.isOrderedList)}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <ListOrdered className="h-3.5 w-3.5" />
            </button>

            <Separator orientation="vertical" className="mx-0.5 h-4 bg-gray-200" />

            <button className={btn(state.isLink)}
              onClick={() => {
                if (state.isLink) {
                  handleUnlink()
                } else {
                  setShowLinkInput(true)
                }
              }}>
              <Link className="h-3.5 w-3.5" />
            </button>

            {noteSlug && onAddComment && (
              <>
                <Separator orientation="vertical" className="mx-0.5 h-4 bg-gray-200" />
                <button className={btn(false)} onClick={handleCommentClick} title="Add comment">
                  <MessageSquare className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </>
        )}
      </div>
    </BubbleMenu>
  )
}
